/**
 * The width of the orcaloops frame's left panel, which a person resizes by
 * dragging its edge.
 *
 * The width starts at {@link LEFT_WIDTH_DEFAULT}, is always held inside
 * {@link LEFT_WIDTH_MIN}–{@link LEFT_WIDTH_MAX}, and is kept in the browser
 * under {@link ORCALOOPS_LEFT_WIDTH_KEY} the same way the theme is.
 * {@link OrcaloopsFrame} writes it as `--ol-left-w` on `.ol-body`, which is
 * what `orcaloops.css` sizes the left track from.
 *
 * SSR/jsdom-safe: every `window` / `localStorage` access is guarded, and the
 * pure {@link computeInitialLeftWidth} takes the window as an argument so the
 * decision is unit-tested without a browser. Mirrors `orcaloops-theme.ts`.
 *
 * No `useState`, no `useEffect`.
 *
 * @since 1.0.0
 */
import { Atom } from "@effect-atom/atom-react";

import type { ThemeWindowLike } from "./clawinstall-theme.ts";

/** The browser-storage key a chosen width is kept under. @since 1.0.0 */
export const ORCALOOPS_LEFT_WIDTH_KEY = "orcaloops-left-width";

/** The width the panel starts at, and returns to on a double-click. @since 1.0.0 */
export const LEFT_WIDTH_DEFAULT = 240;

/** The narrowest the panel goes. @since 1.0.0 */
export const LEFT_WIDTH_MIN = 200;

/** The widest the panel goes. @since 1.0.0 */
export const LEFT_WIDTH_MAX = 480;

/** A width held inside {@link LEFT_WIDTH_MIN}–{@link LEFT_WIDTH_MAX}, to the whole pixel. @since 1.0.0 */
export const clampLeftWidth = (width: number): number =>
  Math.min(LEFT_WIDTH_MAX, Math.max(LEFT_WIDTH_MIN, Math.round(width)));

/**
 * The width to start at: the stored width, clamped, when it is an integer;
 * else {@link LEFT_WIDTH_DEFAULT}. `null` (no window: the server) is the
 * default.
 *
 * @since 1.0.0
 */
export const computeInitialLeftWidth = (win: ThemeWindowLike | null): number => {
  const stored = win?.localStorage?.getItem(ORCALOOPS_LEFT_WIDTH_KEY);
  if (stored === undefined || stored === null || !/^\d+$/.test(stored)) return LEFT_WIDTH_DEFAULT;
  return clampLeftWidth(Number(stored));
};

/**
 * {@link computeInitialLeftWidth} over the real window, or the default on the
 * server. Read after mount, never at import.
 *
 * @since 1.0.0
 */
export const getInitialOrcaloopsLeftWidth = (): number => {
  if (typeof window === "undefined") return computeInitialLeftWidth(null);
  return computeInitialLeftWidth(window as ThemeWindowLike);
};

/** Keep a chosen width so the next visit starts at it. A no-op on the server. @since 1.0.0 */
export const persistOrcaloopsLeftWidth = (width: number): void => {
  if (typeof window === "undefined") return;
  (window as ThemeWindowLike).localStorage?.setItem(ORCALOOPS_LEFT_WIDTH_KEY, String(width));
};

/**
 * The live width. Kept alive so a resize survives every route change on the
 * member surface. It starts at {@link LEFT_WIDTH_DEFAULT} on both sides, so the
 * client's first frame matches the server's; {@link OrcaloopsFrame} applies the
 * stored width ({@link getInitialOrcaloopsLeftWidth}) after mount.
 *
 * @since 1.0.0
 */
export const orcaloopsLeftWidthAtom: Atom.Writable<number> = Atom.make<number>(LEFT_WIDTH_DEFAULT).pipe(
  Atom.keepAlive,
);
